import { useCallback, useContext, useEffect, useMemo } from 'react';
import TextInputDialogContext from './components/TextInputDialogContext';
import type { TextInputDialogOptions, TextInputDialogResult } from './types';

let idCounter = 0;

const useTextInputDialogId = () => {
  const id = useMemo(() => {
    return idCounter++;
  }, []);

  return `dialog-text-input-${id}`;
};

/**
 * Hook to open a dialog with text inputs and get the results asynchronously.
 * Supports string, number, and boolean input types.
 * @example
 * ```tsx
 * interface UserDetails {
 *   firstName: string;
 *   lastName: string;
 *   age: number;
 *   subscribe: boolean;
 * }
 *
 * function MyComponent() {
 *   const getTextInputDialog = useTextInputDialog();
 *
 *   const handleClick = async () => {
 *     const result = await getTextInputDialog<UserDetails>({
 *       inputs: [
 *         { name: 'firstName', type: 'string' },
 *         { name: 'lastName', type: 'string' },
 *         { name: 'age', type: 'number', value: 0 },
 *         { name: 'subscribe', type: 'boolean', value: false },
 *       ],
 *       dialogBoxProps: {
 *         title: 'Enter User Details',
 *       },
 *     });
 *
 *     if (result) {
 *       console.log(result.firstName, result.lastName, result.age, result.subscribe);
 *     } else {
 *       console.log('User cancelled');
 *     }
 *   };
 *
 *   return <button onClick={handleClick}>Open Dialog</button>;
 * }
 * ```
 * @returns A function that opens the dialog and returns a promise with the input values or null if cancelled.
 */
export function useTextInputDialog() {
  const parentId = useTextInputDialogId();
  const { openTextInputDialogBase, closeTextInputDialogOnParentUnmount } =
    useContext(TextInputDialogContext);

  const getTextInputDialog = useCallback(
    <
      T extends Record<string, string | number | boolean> = Record<
        string,
        string | number | boolean
      >,
    >(
      options: TextInputDialogOptions,
    ): Promise<TextInputDialogResult<T>> => {
      return openTextInputDialogBase<T>(parentId, options);
    },
    [parentId, openTextInputDialogBase],
  );

  // When the component calling useTextInputDialog is unmounted, we automatically
  // close the associated dialog. Note that we use a unique id per each
  // useTextInputDialog usage, so that we don't close the dialog when an
  // unrelated component unmounts
  useEffect(() => {
    return () => {
      closeTextInputDialogOnParentUnmount(parentId);
    };
  }, [parentId, closeTextInputDialogOnParentUnmount]);

  return getTextInputDialog;
}
